import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, inject, NgModule } from '@angular/core';
import { HttpEventType, HttpResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { of, defer } from 'rxjs';
import { switchMap, catchError, mapTo } from 'rxjs/operators';
import { DOCUMENT, CommonModule } from '@angular/common';

class CacheController {
}

class CacheInterceptor {
    constructor(cacheController, errorHandler, doc) {
        this.cacheController = cacheController;
        this.errorHandler = errorHandler;
        this.doc = doc;
    }
    intercept(request, next) {
        const { method, urlWithParams } = request;
        if (method !== 'GET') {
            return next.handle(request);
        }
        return this.cacheController.get(urlWithParams).pipe(switchMap((cachedResponse) => {
            if (cachedResponse) {
                return of(cachedResponse);
            }
            return next.handle(request).pipe(switchMap((response) => {
                if (response.type === HttpEventType.Response &&
                    response instanceof HttpResponse &&
                    response.status >= 200 &&
                    response.status < 400) {
                    return this.cacheController.set(urlWithParams, response).pipe(catchError((err) => {
                        this.errorHandler.handleError(err);
                        return of(undefined);
                    }), mapTo(response));
                }
                return of(response);
            }));
        }));
    }
}
CacheInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: CacheInterceptor, deps: [{ token: CacheController }, { token: i0.ErrorHandler }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CacheInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: CacheInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: CacheInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CacheController }, { type: i0.ErrorHandler }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class LRUCache extends CacheController {
    constructor({ maxAge, maxSize } = {}) {
        super();
        this._cache = new Map();
        this.maxAge = maxAge !== null && maxAge !== void 0 ? maxAge : Infinity;
        this.maxSize = maxSize !== null && maxSize !== void 0 ? maxSize : Infinity;
    }
    get(key, notFoundValue = null) {
        return defer(() => {
            var _a;
            let value = (_a = this._cache.get(key)) === null || _a === void 0 ? void 0 : _a.getValue();
            if (value == null) {
                this.delete(key);
                value = notFoundValue;
            }
            return Promise.resolve(value);
        });
    }
    set(key, value, maxAge = this.maxAge) {
        return defer(() => {
            this.deleteFirstKeyIfHaveTo();
            this._cache.set(key, new LRUCacheEntity(key, value, maxAge));
            return Promise.resolve();
        });
    }
    size() {
        return of(this.sizeSync());
    }
    delete(key) {
        this._cache.delete(key);
    }
    sizeSync() {
        return this._cache.size;
    }
    deleteFirstKeyIfHaveTo() {
        if (this.sizeSync() >= this.maxSize) {
            const { value: firstKey } = this._cache.keys().next();
            if (firstKey) {
                this.delete(firstKey);
            }
        }
    }
}
class LRUCacheEntity {
    constructor(key, _value, maxAge = Infinity) {
        this.key = key;
        this._value = _value;
        this.maxAge = maxAge;
        this._createdDate = Date.now();
    }
    getValue() {
        if (this._value == null || this._createdDate + this.maxAge <= Date.now()) {
            this._value = null;
        }
        return this._value;
    }
}

const LRU_CACHE_OPTIONS = new InjectionToken('LRU Cache Options', {
    factory() {
        return {
            maxSize: Infinity,
            maxAge: Infinity,
        };
    },
});
function lruCacheFactory() {
    return new LRUCache(inject(LRU_CACHE_OPTIONS));
}

class NgxSsrCacheModule {
    static configLruCache(options) {
        return {
            ngModule: NgxSsrCacheModule,
            providers: [
                {
                    provide: CacheController,
                    useFactory: lruCacheFactory,
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: CacheInterceptor,
                    multi: true,
                },
                options
                    ? {
                        provide: LRU_CACHE_OPTIONS,
                        useValue: options,
                    }
                    : [],
            ],
        };
    }
}
NgxSsrCacheModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: NgxSsrCacheModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxSsrCacheModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: NgxSsrCacheModule, imports: [CommonModule] });
NgxSsrCacheModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: NgxSsrCacheModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.8", ngImport: i0, type: NgxSsrCacheModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CacheController, CacheInterceptor, LRUCache, LRU_CACHE_OPTIONS, NgxSsrCacheModule, lruCacheFactory };
